import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { createUri, Api } from '@api/utils';
import type {
    ProductCatalog,
    ProductCatalogRequest,
    ProductCatalogListResponse,
    BusinessCentralProducts,
    ProductCatalogOrderRequest,
} from '@/types/productCatalog';
import { OrgUrls } from '@api/org';
import { Query, Mutation } from './utils';

enum Tag {
    ProductCatalog = 'productCatalog',
    ProductCatalogs = 'productCatalogs',
    BusinessCentralProducts = 'businessCentralProducts',
}

export const productCatalogHooks = createApi({
    reducerPath: 'productCatalogApi',
    tagTypes: Object.values(Tag),
    baseQuery: fetchBaseQuery({
        baseUrl: createUri(Api.Orgs, '/org/V1'),
    }),
    endpoints: (build) => ({
        businessCentralProducts: build.query<BusinessCentralProducts, { token: string }>({
            query: ({ token }) => ({
                url: OrgUrls.BusinessCentralProducts,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: [Tag.BusinessCentralProducts],
        }),
        productCatalogs: build.query<ProductCatalog[], { token: string }>({
            query: ({ token }) => ({
                url: OrgUrls.ProductCatalogs,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }),
            providesTags: (result) => [
                Tag.ProductCatalogs,
                ...(result || []).map((product) => ({
                    type: Tag.ProductCatalog,
                    id: product.ProductSKU,
                })),
            ],
        }),
        productCatalog: build.query<ProductCatalog, { token: string; productSKU: string }>({
            query: ({ token, productSKU }) => ({
                url: OrgUrls.ProductCatalog,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    ProductSKU: productSKU,
                },
            }),
            providesTags: (_, __, { productSKU }) => [{ type: Tag.ProductCatalog, id: productSKU }],
        }),
        createProductCatalog: build.mutation<
            ProductCatalog,
            { token: string; productCatalog: ProductCatalogRequest }
        >({
            query: ({ token, productCatalog }) => ({
                url: OrgUrls.ProductCatalog,
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: productCatalog,
            }),
            invalidatesTags: [Tag.ProductCatalogs, Tag.ProductCatalog],
        }),
        updateProductCatalog: build.mutation<
            ProductCatalog,
            { token: string; productCatalog: ProductCatalogRequest }
        >({
            query: ({ token, productCatalog }) => ({
                url: OrgUrls.ProductCatalog,
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: productCatalog,
            }),
            invalidatesTags: (_, __, { productCatalog }) => [
                Tag.ProductCatalogs,
                { type: Tag.ProductCatalog, id: productCatalog.ProductSKU },
            ],
        }),
        updateProductCatalogOrder: build.mutation<
            { success: boolean; updated: number },
            { token: string; orders: ProductCatalogOrderRequest['orders'] }
        >({
            query: ({ token, orders }) => ({
                url: OrgUrls.ProductCatalogOrder,
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: { orders },
            }),
            invalidatesTags: [Tag.ProductCatalogs],
        }),
    }),
});

const productCatalog = {
    useBusinessCentralProductsQuery: Query.withToken(productCatalogHooks.useBusinessCentralProductsQuery),
    useProductCatalogsQuery: Query.withToken(productCatalogHooks.useProductCatalogsQuery),
    useProductCatalogQuery: Query.withToken(productCatalogHooks.useProductCatalogQuery),
    useCreateProductCatalogMutation: Mutation.withToken(productCatalogHooks.useCreateProductCatalogMutation),
    useUpdateProductCatalogMutation: Mutation.withToken(productCatalogHooks.useUpdateProductCatalogMutation),
    useUpdateProductCatalogOrderMutation: Mutation.withToken(
        productCatalogHooks.useUpdateProductCatalogOrderMutation
    ),
};

export const {
    useBusinessCentralProductsQuery,
    useProductCatalogsQuery,
    useProductCatalogQuery,
    useCreateProductCatalogMutation,
    useUpdateProductCatalogMutation,
    useUpdateProductCatalogOrderMutation,
} = productCatalog;
